import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productAPI, categoryAPI } from '../api/services';
import ProductCard from '../components/product/ProductCard';
import Loading from '../components/common/Loading';
import './Shop.css';

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get('page') || '0');
  const categoryId = searchParams.get('categoryId') || '';
  const gender = searchParams.get('gender') || '';
  const brand = searchParams.get('brand') || '';
  const minPrice = searchParams.get('minPrice') || '';
  const maxPrice = searchParams.get('maxPrice') || '';
  const q = searchParams.get('q') || '';

  useEffect(() => {
    Promise.all([categoryAPI.getAll(), productAPI.getBrands()])
      .then(([catRes, brandRes]) => {
        setCategories(catRes.data.data || []);
        setBrands(brandRes.data.data || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page, size: 12 };
    if (categoryId) params.categoryId = categoryId;
    if (gender) params.gender = gender;
    if (brand) params.brand = brand;
    if (minPrice) params.minPrice = minPrice;
    if (maxPrice) params.maxPrice = maxPrice;
    if (q) params.q = q;

    productAPI.search(params)
      .then(res => {
        setProducts(res.data.data.content || []);
        setTotalPages(res.data.data.totalPages || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, categoryId, gender, brand, minPrice, maxPrice, q]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) newParams.set(key, value);
    else newParams.delete(key);
    if (key !== 'page') newParams.set('page', '0');
    setSearchParams(newParams);
  };

  return (
    <div className="shop-page">
      <div className="shop-container">
        <aside className="shop-filters">
          <h3>Filters</h3>

          <div className="filter-group">
            <h4>Category</h4>
            <select value={categoryId} onChange={e => updateFilter('categoryId', e.target.value)}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <h4>Gender</h4>
            <select value={gender} onChange={e => updateFilter('gender', e.target.value)}>
              <option value="">All</option>
              <option value="MEN">Men</option>
              <option value="WOMEN">Women</option>
              <option value="UNISEX">Unisex</option>
              <option value="KIDS">Kids</option>
            </select>
          </div>

          <div className="filter-group">
            <h4>Brand</h4>
            <select value={brand} onChange={e => updateFilter('brand', e.target.value)}>
              <option value="">All Brands</option>
              {brands.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div className="filter-group">
            <h4>Price Range</h4>
            <div className="price-inputs">
              <input type="number" placeholder="Min" value={minPrice}
                onChange={e => updateFilter('minPrice', e.target.value)} />
              <span>–</span>
              <input type="number" placeholder="Max" value={maxPrice}
                onChange={e => updateFilter('maxPrice', e.target.value)} />
            </div>
          </div>

          <button className="btn btn-outline btn-sm" onClick={() => setSearchParams({})}>
            Clear Filters
          </button>
        </aside>

        <main className="shop-content">
          {q && <h2 className="search-heading">Results for "{q}"</h2>}

          {loading ? <Loading /> : products.length === 0 ? (
            <div className="empty-state">
              <h3>No products found</h3>
              <p>Try adjusting your filters or search query.</p>
            </div>
          ) : (
            <>
              <div className="product-grid">{products.map(p => <ProductCard key={p.id} product={p} />)}</div>
              {totalPages > 1 && (
                <div className="pagination">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button key={i} className={`page-btn ${i === page ? 'active' : ''}`}
                      onClick={() => updateFilter('page', String(i))}>
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
